/**
 * Extract city and state from a location string
 * Handles formats like:
 * - "Mumbai, Maharashtra, India"
 * - "Mumbai, India"
 * - "Mumbai"
 * - "7RR2+CR5, Dhebar Rd, Raghuveer Para, Rajkot, Gujarat 360001" (Google Places format)
 * - "New Delhi, Delhi, India"
 * - "B-20 Madhuvan Society, Street no-1... Golden Park, Rajkot, Gujarat 360005"
 * - "Rajkot International Airport" → Extracts "Rajkot" as city
 * - "Mumbai Airport, Maharashtra" → Extracts "Mumbai" as city
 *
 * @param {string} locationString - Full location string (usually from Google Places API)
 * @returns {Object} - Object containing city and state
 */
export const extractCityAndState = (locationString) => {
  if (!locationString) {
    return { city: null, state: null };
  }

  // First: Handle airport names - remove airport suffixes to extract just the city
  // Common airport name patterns: "City Airport", "City International Airport", "City Intl Airport", etc.
  const airportPatterns = [
    /\s+(international\s+)?airport(\s+terminal)?/i,
    /\s+(intl\.?|iata|icao)\s+airport/i,
    /\s+airport\s+\(.*?\)/i,
    /\s+(iata|icao):\s*\w+/i,
  ];

  let cleanedLocation = locationString;
  for (const pattern of airportPatterns) {
    if (pattern.test(cleanedLocation)) {
      cleanedLocation = cleanedLocation.replace(pattern, '').trim();
      console.log(`✈️ Airport name detected: "${locationString}" → Cleaned: "${cleanedLocation}"`);
      break;
    }
  }

  const parts = cleanedLocation.split(',').map(part => part.trim());

  let city = null;
  let state = null;

  // Common states in India
  const indianStates = [
    'maharashtra', 'karnataka', 'tamil nadu', 'uttar pradesh', 'west bengal',
    'rajasthan', 'tamil naduni', 'andhra pradesh', 'telangana', 'gujarat',
    'punjab', 'haryana', 'uttarakhand', 'himachal pradesh', 'jharkhand',
    'chhattisgarh', 'goa', 'kerala', 'assam', 'tripura', 'manipur', 'mizoram',
    'nagaland', 'meghalaya', 'delhi', 'delhi ncr'
  ];

  const commonCountries = ['india', 'usa', 'uk', 'united states', 'united kingdom', 'canada', 'australia'];

  // Major Indian cities to prioritize (includes airports for better matching)
  const majorCities = [
    'mumbai', 'delhi', 'bangalore', 'hyderabad', 'kolkata', 'chandigarh', 'pune',
    'ahmedabad', 'jaipur', 'lucknow', 'kanpur', 'nagpur', 'indore', 'thane',
    'bhopal', 'visakhapatnam', 'patna', 'vadodara', 'ghaziabad', 'ludhiana',
    'agra', 'nashik', 'faridabad', 'meerut', 'rajkot', 'varanasi', 'srinagar',
    'aurangabad', 'dhanbad', 'amritsar', 'navi mumbai', 'allahabad', 'ranchi',
    'howrah', 'salem', 'guwahati', 'coimbatore', 'vijayawada', 'jodhpur',
    'madurai', 'raipur', 'kota', 'goa', 'kochi',
    // Airport cities (for fuzzy matching)
    'indira gandhi', 'hartsfield', 'jakarta', 'hong kong', 'incheon', 'pudong',
    'changi', 'schiphol', 'london', 'paris', 'frankfurt', 'doha', 'dubai'
  ];

  // For Google Places full addresses, city and state are usually near the end
  // Search backwards: find state/province, then find city

  // Look for state in all parts (usually near the end, before postal code/country)
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i].toLowerCase();

    // Skip postal codes (numeric) and countries
    if (/^\d+$/.test(parts[i]) || commonCountries.includes(part)) {
      continue;
    }

    // Check if this part matches a state
    if (indianStates.some(st => part.includes(st) || st.includes(part))) {
      state = parts[i];
      break;
    }
  }

  // If we found a state, look for city before it
  if (state) {
    const stateIndex = parts.indexOf(state);

    // Strategy 1: Look for major cities first (more reliable)
    for (let i = stateIndex - 1; i >= 0; i--) {
      const part = parts[i].toLowerCase();
      if (majorCities.some(mc => part.includes(mc) || mc.includes(part))) {
        city = parts[i];
        break;
      }
    }

    // Strategy 2: If no major city found, look for any part that looks like a city
    if (!city) {
      for (let i = stateIndex - 1; i >= Math.max(0, stateIndex - 3); i--) {
        const part = parts[i];
        // Skip location codes (like "7RR2+CR5", "B/1", "A-123") and numeric postal codes
        // Location codes typically have format: letters/numbers+special chars, area codes with /, or are mostly numeric
        const isLocationCode = /^[A-Z0-9]+[+/\-]|^\d{1,3}\/\d+|^[A-Z]\-\d/.test(part) || /^\d+$/.test(part);
        if (!isLocationCode && part.length > 2) {
          city = part;
          break;
        }
      }
    }
  }

  // Fallback: if no state found, assume format is "city" or "city, state"
  if (!city && !state && parts.length >= 1) {
    // For airport locations without explicit state, extract first word as city
    // e.g., "Rajkot" from cleaned "Rajkot" (was "Rajkot International Airport")
    const firstPart = parts[0].toLowerCase();

    // Check if first part matches a major city
    if (majorCities.some(mc => firstPart.includes(mc) || mc.includes(firstPart))) {
      city = parts[0];
    } else {
      // Default: use first part as city if it's not too long and doesn't look like an address
      const isAddressComponent = /^[A-Z0-9]+[+/\-]|^\d+|^[A-Z]\-/.test(parts[0]);
      if (!isAddressComponent && parts[0].length > 2) {
        city = parts[0];
      }
    }

    // Try to get state from second part
    if (city && parts.length >= 2 && !commonCountries.includes(parts[1].toLowerCase())) {
      state = parts[1];
    }
  }

  return { city, state };
};

/**
 * Strips Google Plus Codes (Open Location Codes) like "7RR2+CR5, " or "7PMP+W72, "
 * from an address string.
 */
export const stripPlusCode = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/^[A-Z0-9]{4,8}\+[A-Z0-9]{2,}\s*,?\s*/i, '') // beginning of string
    .replace(/,\s*[A-Z0-9]{4,8}\+[A-Z0-9]{2,}\s*,?\s*/gi, ', ') // middle of string
    .trim();
};

/**
 * Format a place selection like MakeMyTrip:
 * Ensures the human-readable Place / POI / Station Name is preserved as the primary title,
 * strips any cryptic Google Plus Codes, and returns a clean, original address.
 */
export const formatPlaceAddress = (place, suggestion) => {
  const mainName = (
    place?.name ||
    suggestion?.structured_formatting?.main_text ||
    suggestion?.name ||
    ''
  ).trim();

  let rawAddr = (
    place?.formatted_address ||
    suggestion?.description ||
    ''
  ).trim();

  let cleanAddr = stripPlusCode(rawAddr);

  if (!cleanAddr && mainName) return mainName;
  if (!mainName) return cleanAddr;

  const cleanLower = cleanAddr.toLowerCase();
  const mainLower = mainName.toLowerCase();

  // If clean address already begins with place name, don't duplicate
  if (cleanLower.startsWith(mainLower)) {
    return cleanAddr;
  }

  // Prepend place name so place is always the first item before comma (MakeMyTrip style)
  return `${mainName}, ${cleanAddr}`;
};

/**
 * Extracts display parts for UI (MakeMyTrip style):
 * { main: "Rajkot Central Bus Station - Bus port", sub: "Dhebar Rd, Rajkot, Gujarat" }
 */
export const formatLocationDisplay = (locationString) => {
  if (!locationString || typeof locationString !== 'string') {
    return { main: '', sub: '' };
  }
  const cleaned = stripPlusCode(locationString);
  const parts = cleaned.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return { main: '', sub: '' };
  if (parts.length === 1) return { main: parts[0], sub: '' };
  return {
    main: parts[0],
    sub: parts.slice(1).join(', ')
  };
};
